import L from 'leaflet';
import { BaseLayers } from './layers';
import { Overlays } from './overlays';
import { encodeObjectForUrl, decodeObjectFromUrl } from '../util/urlEncoding';

export interface UrlState {
    filter: string;
    unit: string;
    level: string;
    mode: string;
    overlays: string[];
    searchPattern?: Record<string, string>; // Store decoded search pattern object
    passagePlan?: Record<string, any>; // Store decoded passage plan object
}

export function constructUrl(
    baseLayerName: string,
    overlays: string[],
    filter: string,
    unit: string,
    level: string,
    mode: string,
    center: L.LatLng,
    zoom: number,
    searchPattern?: Record<string, string>,
    passagePlan?: Record<string, any>
): string {
    const params = new URLSearchParams();

    params.set('lat', center.lat.toFixed(5));
    params.set('lng', center.lng.toFixed(5));
    params.set('zoom', zoom.toString());

    if (baseLayerName) {
        params.set('baseLayer', baseLayerName);
    }

    if (overlays.length > 0) {
        params.set('overlays', overlays.join(','));
    }

    if (filter) {
        params.set('filter', filter);
    }

    if (unit && unit !== 'none') {
        params.set('unit', unit);
    }

    if (level) {
        params.set('level', level);
    }

    if (mode) {
        params.set('mode', mode);
    }

    // Add search pattern as a single encoded parameter
    if (searchPattern) {
        params.set('psp', encodeObjectForUrl(searchPattern));
    }

    // Add passage plan as a single encoded parameter
    if (passagePlan) {
        params.set('ppp', encodeObjectForUrl(passagePlan));
    }

    return `${window.location.pathname}?${params.toString()}`;
}

export function updateUrlFromMapState(
    map: L.Map,
    baseLayers: BaseLayers,
    overlays: Overlays,
    filter: string,
    unit: string = '',
    level: string = '',
    mode: string = '',
    searchPattern?: Record<string, string>,
    passagePlan?: Record<string, any>
) {
    // Determine active base layer
    let activeBaseLayerName = '';
    map.eachLayer((layer) => {
        for (const [name, baseLayer] of Object.entries(baseLayers)) {
            if (layer === baseLayer) {
                activeBaseLayerName = name;
                break;
            }
        }
    });

    // Determine active overlays from the map
    const activeOverlays: string[] = [];
    map.eachLayer((layer) => {
        for (const [name, overlay] of Object.entries(overlays)) {
            if (layer === overlay) {
                activeOverlays.push(name);
                break;
            }
        }
    });

    const newUrl = constructUrl(
        activeBaseLayerName,
        activeOverlays,
        filter,
        unit,
        level,
        mode,
        map.getCenter(),
        map.getZoom(),
        searchPattern,
        passagePlan
    );

    window.history.replaceState({}, '', newUrl);
}

export function getOverlaysFromUrl(): string[] {
    const params = new URLSearchParams(window.location.search);
    const overlaysParam = params.get('overlays');
    const popupParam = params.get('popup');

    const allOverlaysList: string[] = [];

    if (overlaysParam) {
        overlaysParam.split(',').forEach(name => allOverlaysList.push(name));
    }
    if (popupParam) {
        popupParam.split(',').forEach(name => {
            if (!allOverlaysList.includes(name)) {
                allOverlaysList.push(name);
            }
        });
    }
    return allOverlaysList;
}

export function applyMapStateFromUrl(
    map: L.Map,
    baseLayers: BaseLayers,
    overlays: Overlays,
    mapFilterInput: HTMLInputElement
): UrlState {
    const params = new URLSearchParams(window.location.search);

    // Apply center and zoom
    const lat = parseFloat(params.get('lat') || '');
    const lng = parseFloat(params.get('lng') || '');
    const zoom = parseInt(params.get('zoom') || '', 10);

    if (!isNaN(lat) && !isNaN(lng) && !isNaN(zoom)) {
        map.setView([lat, lng], zoom);
    }

    // Apply base layer
    const baseLayerName = params.get('baseLayer');
    if (baseLayerName && baseLayers[baseLayerName as keyof BaseLayers]) {
        const layerToAdd = baseLayers[baseLayerName as keyof BaseLayers];
        Object.values(baseLayers).forEach(layer => {
            if (map.hasLayer(layer)) {
                map.removeLayer(layer);
            }
        });
        map.addLayer(layerToAdd);
    }

    // Apply filter
    const filter = params.get('filter') || '';
    mapFilterInput.value = filter;

    // Get other params
    const unit = params.get('unit') || '';
    const level = params.get('level') || '';
    const mode = params.get('mode') || '';

    // Decode passage plan
    const encodedPassagePlan = params.get('ppp');
    const passagePlan = encodedPassagePlan ? decodeObjectFromUrl(encodedPassagePlan) : undefined;

    const allOverlaysList = getOverlaysFromUrl();

    allOverlaysList.forEach(name => {
        const overlay = overlays[name as keyof Overlays];
        if (overlay && !map.hasLayer(overlay)) {
            map.addLayer(overlay);
        }
    });

    // Decode search pattern
    const encodedSearchPattern = params.get('psp');
    const searchPattern = encodedSearchPattern ? decodeObjectFromUrl(encodedSearchPattern) : undefined;

    return {
        filter,
        unit,
        level,
        mode,
        overlays: allOverlaysList,
        searchPattern: searchPattern,
        passagePlan: passagePlan
    };
}
